import { Pressable, StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import { formatFileSize } from '@/lib/fileSize';
import type { StepVideo } from '@/types/deal';
import type { VideoAdStats as Stats } from '@/types/voltr';

import { UploadFileTile } from './UploadFileTile';
import { VideoAdStats } from './VideoAdStats';

export type SocialVideoCardProps = {
  video: StepVideo;
  /** Voltr's still (signed for an hour); a film tile when there's none. */
  thumbnail?: string;
  /** How it does as an ad, once a brand runs it; nothing until then. */
  ads?: Stats;
  onReplace?: (video: StepVideo) => void;
  onRemove?: (video: StepVideo) => void;
  /** Being removed: its buttons wait. */
  busy?: boolean;
  /** One of several sent back: Replace is outlined, not the page's loud green. */
  quiet?: boolean;
  /** The card's View, so the hero's "Fix 1 video" can scroll to it. */
  cardRef?: (view: View | null) => void;
  testID?: string;
};

/**
 * One video on the upload step, in the Figma's file-card look: tile, name,
 * size, the server's word for where it stands (coloured by its tone), the coach's feedback (amber only when it's sent back) and, for a
 * sent-back video, its own Replace and Remove. VoiceOver reads the card as
 * one sentence; the buttons stay separate elements.
 */
export function SocialVideoCard({ video, thumbnail, ads, onReplace, onRemove, busy, quiet, cardRef, testID }: SocialVideoCardProps) {
  const notes = video.notes.length ? ` Coach feedback: ${video.notes.join(' ')}` : '';
  const replace = video.actions?.find((a) => a.kind === 'replace');
  const remove = video.actions?.find((a) => a.kind === 'remove');
  const size = formatFileSize(video.size_bytes);

  return (
    <View ref={cardRef} style={styles.card} testID={testID}>
      <View accessible accessibilityLabel={`${[video.name, size, video.status.label].filter(Boolean).join(', ')}.${notes}`} style={styles.info}>
        <View style={styles.head}>
          <UploadFileTile still={thumbnail} testID={testID} />
          {/* Name, size, status beside the tile, as on the upload card. */}
          <View style={styles.titles}>
            <Text variant="bodyBold" color="deepGreen" numberOfLines={1}>
              {video.name}
            </Text>
            {size ? (
              <Text variant="caption" color="textMuted">
                {size}
              </Text>
            ) : null}
            <Text variant="captionBold" color={statusColor(video.status.tone)} testID={testID ? `${testID}-status` : undefined}>
              {video.status.label}
            </Text>
          </View>
        </View>
        {video.notes.length > 0 ? (
          <View style={[styles.notes, video.sent_back ? styles.notesSentBack : styles.notesPlain]}>
            <Text variant="captionBold" color="text">
              Coach feedback
            </Text>
            {video.notes.map((note, i) => (
              <Text key={i} variant="caption" color="text">
                {note}
              </Text>
            ))}
          </View>
        ) : null}
      </View>
      {ads ? <VideoAdStats stats={ads} testID={testID ? `${testID}-ads` : undefined} /> : null}
      {replace && onReplace ? (
        <Button label={replace.label} variant={quiet ? 'outline' : 'primary'} fullWidth disabled={busy} onPress={() => onReplace(video)} testID={testID ? `${testID}-replace` : undefined} />
      ) : null}
      {remove && onRemove ? (
        <Pressable
          onPress={() => onRemove(video)}
          disabled={busy}
          accessibilityRole="button"
          accessibilityState={{ disabled: !!busy, busy: !!busy }}
          accessibilityLabel={`${remove.label} ${video.name}`}
          hitSlop={REMOVE_HIT_SLOP}
          style={({ pressed }) => [styles.remove, pressed && styles.pressed]}
          testID={testID ? `${testID}-remove` : undefined}
        >
          <Text variant="captionBold" color="textMuted">
            {remove.label}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

/** The server's tone for a status → its text colour. Own keys only: a tone is server-sent. */
type StatusColor = 'accentText' | 'warnPillText' | 'danger' | 'deepGreen';
const TONE: Record<string, StatusColor> = { success: 'accentText', warning: 'warnPillText', danger: 'danger' };

function statusColor(tone: string): StatusColor {
  return (Object.prototype.hasOwnProperty.call(TONE, tone) && TONE[tone]) || 'deepGreen';
}

// The remove label is a 16pt line; 14pt above and below make the 44pt target.
const REMOVE_HIT_SLOP = { top: 14, bottom: 14, left: 16, right: 16 };

const styles = StyleSheet.create({
  card: {
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  info: { gap: spacing.sm },
  head: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  titles: { flex: 1, gap: spacing.xs },
  notes: { gap: spacing.xs, padding: spacing.md, borderRadius: radii.base, borderWidth: StyleSheet.hairlineWidth },
  notesSentBack: { backgroundColor: colors.warnPillBg, borderColor: colors.warnPillBorder },
  notesPlain: { backgroundColor: colors.surfaceSunken, borderColor: colors.border },
  remove: { alignSelf: 'center', paddingVertical: spacing.xs },
  pressed: { opacity: 0.6 },
});
